import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Zap, Check, CreditCard, Download, ExternalLink, ArrowRight,
  AlertTriangle, Loader2, FileText,
} from "lucide-react";
import { PageContainer, PageHeader, Card } from "../components/kit";
import { PLAN_FEATURES, MONTHLY_PRICE, YEARLY_PRICE, planFeatureText } from "../lib/plans";
import UpgradeModal from "../components/UpgradeModal";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

const fmtMoney = (amount, currency) =>
  amount == null ? "—" : `$${Number(amount).toFixed(2)}${currency && currency !== "USD" ? ` ${currency}` : ""}`;

const STATUS_META = {
  active:   { label: "Active",        cls: "bg-[#10B981]/10 text-[#10B981]" },
  trialing: { label: "Trial",         cls: "bg-[#0025D8]/10 text-[#0025D8]" },
  past_due: { label: "Past due",      cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  unpaid:   { label: "Unpaid",        cls: "bg-[#E11D48]/10 text-[#E11D48]" },
  canceled: { label: "Canceled",      cls: "bg-muted text-muted-foreground" },
  paused:   { label: "Paused",        cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
};

const INVOICE_STATUS_CLS = {
  paid:          "bg-[#10B981]/10 text-[#10B981]",
  open:          "bg-[#F59E0B]/10 text-[#F59E0B]",
  void:          "bg-muted text-muted-foreground",
  uncollectible: "bg-[#E11D48]/10 text-[#E11D48]",
  draft:         "bg-muted text-muted-foreground",
};

export default function Subscription() {
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/stripe/subscription");
      setSub(data);
    } catch {
      toast.error("Could not load subscription details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sessionId = params.get("session_id");
    const upgraded = params.get("upgraded") === "1";

    // Clean URL params immediately
    const cleaned = new URLSearchParams(params);
    cleaned.delete("upgraded");
    cleaned.delete("session_id");
    setParams(cleaned, { replace: true });

    if (upgraded && sessionId) {
      // Verify payment directly with Stripe and upgrade user —
      // this is instant and doesn't depend on the webhook arriving.
      (async () => {
        try {
          await api.post("/stripe/verify-session", { session_id: sessionId });
          toast.success("Welcome to Premium!");
        } catch (err) {
          const msg = err?.response?.data?.detail || err?.message || "Unknown error";
          if (!msg.includes("already_premium")) {
            toast.error(`Verification failed: ${msg}`, { duration: 10000 });
          }
        } finally {
          load();
        }
      })();
    } else {
      load();
      if (upgraded) toast.success("Welcome to Premium!");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/stripe/portal-session");
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer width="narrow">
        <div className="py-20 flex items-center justify-center text-muted-foreground font-mono text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading subscription…
        </div>
      </PageContainer>
    );
  }

  const isPremium = sub?.plan === "premium";
  const statusMeta = STATUS_META[sub?.status] || null;
  const pm = sub?.payment_method;

  return (
    <PageContainer width="narrow">
      <PageHeader eyebrow="Account" title="Subscription & billing." />

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      {/* ── Plan summary card ── */}
      {isPremium ? (
        <Card padding="p-0" className="overflow-hidden mb-6">
          <div className="relative bg-[#0F172A] px-6 py-6 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)" }} />
            <div className="relative flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/20 px-2.5 py-1 mb-3">
                  <Zap className="w-3 h-3 text-[#A78BFA]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A78BFA] font-bold">Premium</span>
                  {statusMeta && (
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ml-1 ${statusMeta.cls}`}>
                      {statusMeta.label}
                    </span>
                  )}
                </div>
                <div className="font-display font-black text-3xl text-white">
                  {sub?.amount != null
                    ? <>{fmtMoney(sub.amount, sub.currency)}<span className="text-base font-bold text-white/40"> /{sub.interval || "mo"}</span></>
                    : "Premium"}
                </div>
                <p className="text-xs text-white/50 mt-1.5 font-mono">
                  {sub?.cancel_at_period_end
                    ? `Cancels on ${fmtDate(sub.current_period_end)}`
                    : sub?.current_period_end
                      ? `Renews on ${fmtDate(sub.current_period_end)}`
                      : "Unlimited interviews · all roles · no session limit"}
                </p>
              </div>
            </div>

            {sub?.cancel_at_period_end && (
              <div className="relative mt-4 flex items-start gap-2 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-3 py-2.5">
                <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#FCD34D] leading-snug">
                  Your subscription is set to cancel. You'll keep Premium until {fmtDate(sub.current_period_end)}, then move to the free plan.
                </p>
              </div>
            )}
          </div>

          {/* Payment method + manage */}
          <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2.5 text-sm">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              {pm ? (
                <span className="text-[#0F172A]">
                  <span className="capitalize font-medium">{pm.brand}</span> ···· {pm.last4}
                  <span className="text-muted-foreground font-mono text-xs ml-2">
                    exp {String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">No card on file</span>
              )}
            </div>
            <button onClick={openPortal} disabled={portalLoading}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-4 py-2.5 text-xs font-bold transition-colors disabled:opacity-60">
              {portalLoading
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</>
                : <>Manage subscription <ExternalLink className="w-3.5 h-3.5" /></>}
            </button>
          </div>
          <p className="px-6 pb-4 -mt-1 font-mono text-[10px] text-muted-foreground">
            Update your card, switch billing cycle, or cancel — securely via Stripe.
          </p>
        </Card>
      ) : (
        /* ── Free plan upsell ── */
        <Card className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Current plan</div>
              <div className="font-display font-black text-2xl text-[#0F172A]">Free</div>
              <p className="text-xs text-muted-foreground mt-1.5">2 interviews / month · 1 saved resume</p>
            </div>
            <button onClick={() => setUpgradeOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-4 py-2.5 text-xs font-bold transition-colors">
              <Zap className="w-3.5 h-3.5" /> Upgrade to Premium
            </button>
          </div>

          {/* What you get */}
          <div className="mt-5 pt-5 border-t border-border">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED] mb-3">Premium unlocks</div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {PLAN_FEATURES.map((f) => (
                <div key={f.label} className="flex items-center gap-2.5">
                  <Check className="w-3.5 h-3.5 text-[#7C3AED] flex-shrink-0" />
                  <span className="text-[13px] text-[#0F172A]">
                    {planFeatureText(f, "premium")}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">
              From <span className="font-semibold text-[#0F172A]">${MONTHLY_PRICE}/mo</span> or <span className="font-semibold text-[#0F172A]">${YEARLY_PRICE}/yr</span> — cancel anytime.
            </p>
          </div>
        </Card>
      )}

      {/* ── Invoices ── */}
      <Card padding="p-0" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-display font-bold text-base">Billing history</h2>
        </div>

        {!sub?.invoices?.length ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {isPremium ? "No invoices yet — your first one will appear after the next billing cycle." : "No billing history. Invoices appear here once you subscribe."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sub.invoices.map((inv, i) => {
              const stCls = INVOICE_STATUS_CLS[inv.status] || "bg-muted text-muted-foreground";
              return (
                <div key={inv.number || i} className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0F172A]">{fmtDate(inv.date)}</div>
                    {inv.number && <div className="font-mono text-[10px] text-muted-foreground truncate">#{inv.number}</div>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-mono text-sm text-[#0F172A]">{fmtMoney(inv.amount, inv.currency)}</span>
                    {inv.status && (
                      <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${stCls}`}>
                        {inv.status}
                      </span>
                    )}
                    {inv.pdf_url ? (
                      <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#0025D8] hover:underline text-xs font-medium" title="Download PDF">
                        <Download className="w-3.5 h-3.5" /> PDF
                      </a>
                    ) : <span className="w-12" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Help footer */}
      <p className="text-center text-[11px] text-muted-foreground mt-6">
        Payments are processed securely by Stripe. Need help with billing?{" "}
        <a href="mailto:cheems.navi@gmail.com" className="text-[#0025D8] hover:underline">Contact support</a>.
      </p>
    </PageContainer>
  );
}
